// One way to write money, dates, times and stored codes on every screen.
//
// Before this, money appeared as '$1200.80', '$1,200.80', '$27,018' (rounded) and '$-2,557' (sign
// after the dollar) depending on the page, and dates as '2026-10-17' or '17 Oct 2026'. The same
// figure read differently on the dashboard, in a report and in an export, which is how people stop
// trusting the numbers. Amounts are never changed here, only how they are written.

const TWO_DP = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const WHOLE = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });

// '$1,234.50' / '-$2,556.70'. Always to the cent. A value that rounds to zero never shows '-$0.00'.
export const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  const cents = Math.round(v * 100);
  if (cents === 0) return '$0.00';
  return (cents < 0 ? '-$' : '$') + TWO_DP.format(Math.abs(cents) / 100);
};

// For catalogue prices only (plans, add-ons): '$199' when there are no cents, '$548.90' when there
// are. Never use this for an amount someone was charged.
export const moneyPrice = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0';
  const cents = Math.round(v * 100);
  if (cents % 100 === 0) return (cents < 0 ? '-$' : '$') + WHOLE.format(Math.abs(cents) / 100);
  return money(v);
};

// Plain grouped number: 1,234 or 1,234.5 (up to `dp` places).
export const num = (n, dp = 0) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return new Intl.NumberFormat('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);
};

// Short axis labels only (chart ticks): 1.3k, 2.6k, 1.2M. Never for an amount in a table or tile.
export const compact = (n) => new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);

// A 'YYYY-MM-DD' string is a calendar day, not an instant: build it in local time so it can never
// slip to the previous day.
const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

// '7 Feb 2026'
export const fmtDate = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : (v ? String(v) : '—');
};

// '7 Feb' (same year context)
export const fmtDayMonth = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : (v ? String(v) : '—');
};

// '7 Feb 2026, 2:14 pm'
export const fmtDateTime = (v) => {
  const d = toDate(v);
  if (!d) return v ? String(v) : '—';
  return `${fmtDate(d)}, ${d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).toLowerCase()}`;
};

// '14:00' / '14:00:00' → '2:00 pm'. Wall-clock times stay wall-clock: no Date, no time zone.
export const fmtTime = (hm) => {
  if (!hm) return '—';
  const [h, m] = String(hm).split(':').map(Number);
  if (!Number.isFinite(h)) return String(hm);
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};

// '10:00 am–4:00 pm'
export const fmtTimeRange = (a, b) => `${fmtTime(a)}–${fmtTime(b)}`;

// Hours with one decimal: '8.0 h'. An end at or before the start is the next day (overnight).
export const fmtHours = (h) => `${num(h, 1)} h`;
export const hoursBetween = (start, end) => {
  const mins = (t) => { const [h, m] = String(t || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  let d = mins(end) - mins(start);
  if (d <= 0) d += 24 * 60;
  return d / 60;
};

// Relative time for recent events: 'just now', '5 min ago', '3 h ago', 'yesterday', '4 days ago',
// then the date.
export const fmtAgo = (v) => {
  const d = toDate(v);
  if (!d) return '—';
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 172800) return 'yesterday';
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} days ago`;
  return fmtDate(d);
};

// Words the product always writes in capitals.
const ACRONYMS = new Set(['ndis', 'ndia', 'sil', 'sta', 'sc', 'ph', 'gst', 'aba', 'csv', 'pdf', 'km', 'sms',
  'ai', 'evv', 'id', 'abn', 'acn', 'po', 'myob', 'sirs', 'sah', 'hcp', 'prn', 's8', 'wwcc', 'nswc', 'ndiswc',
  'cpr', 'hr', 'mfa', 'url', 'api', 'xero', 'ot', 'pt', 'bsp', 'rp', 'dex', 'qi', 'mar', 'emar']);
const PROPER = { xero: 'Xero' };

// Stored codes → words: 'community_access' → 'Community access', 'cancelled_with_charge' →
// 'Cancelled with charge', 'ndis_managed' → 'NDIS managed'. Human text passes through unchanged.
export const humanise = (code) => {
  if (code == null || code === '') return '';
  const s = String(code);
  if (!/[_-]/.test(s) && /[A-Z ]/.test(s)) return s;       // already written for people
  const words = s.replace(/[_-]+/g, ' ').trim().toLowerCase().split(/\s+/);
  return words.map((w, i) => {
    if (PROPER[w]) return PROPER[w];
    if (ACRONYMS.has(w)) return w.toUpperCase();
    return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w;
  }).join(' ');
};

// Stored codes to words, but only when the value really is a code: 'community_access' → 'Community
// access', 'daily_living' → 'Daily living'. Text already written for people ('Personal Care -
// Weekday') and NDIS support item numbers ('01_011_0107_1_1') are shown exactly as stored.
// Display only: never send the result back to the API in place of the code.
export const codeLabel = (v) => {
  if (v == null || v === '') return '';
  const s = String(v);
  if (/^\d{2}_\d{3}_/.test(s)) return s;
  if (/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/.test(s)) return humanise(s);
  return s;
};

// Codes sitting inside text written for people: 'daily_activities (NDIS Core Supports)' →
// 'Daily activities (NDIS Core Supports)'. Only snake_case words that start with a letter are
// touched, so item numbers and ordinary words pass through; a code mid-sentence stays lower case
// ('added a shift_note' → 'added a shift note'). Display only, like codeLabel.
export const codeWords = (text) => {
  if (text == null || text === '') return text;
  return String(text).replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g, (tok, at) => {
    const words = humanise(tok);
    if (at === 0 || ACRONYMS.has(tok.split('_')[0])) return words;
    return words.charAt(0).toLowerCase() + words.slice(1);
  });
};

// '1 line' / '3 lines' / 'No lines'
export const plural = (n, one, many, none) => {
  const c = Number(n) || 0;
  if (c === 0 && none) return none;
  return `${num(c)} ${c === 1 ? one : (many || `${one}s`)}`;
};

// How a participant's funding is managed. Two stored codes ('ndis_managed' and 'ndia_managed') mean
// the same thing to a coordinator, so both read as 'NDIA-managed'.
export const CLIENT_TYPES = [
  { value: 'ndia_managed', label: 'NDIA-managed' },
  { value: 'plan_managed', label: 'Plan-managed' },
  { value: 'self_managed', label: 'Self-managed' },
  { value: 'aged_care', label: 'Aged care' },
  { value: 'private', label: 'Private' },
];
const CLIENT_TYPE_LABEL = {
  ndis_managed: 'NDIA-managed', ndia_managed: 'NDIA-managed', plan_managed: 'Plan-managed',
  self_managed: 'Self-managed', aged_care: 'Aged care', private: 'Private',
};
export const clientTypeLabel = (code) => CLIENT_TYPE_LABEL[code] || humanise(code) || '—';

// A person's initials, for avatars.
export const initialsOf = (first, last) => {
  const f = String(first || '').trim(); const l = String(last || '').trim();
  if (!f && !l) return '?';
  if (!l) return f.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return `${f[0] || ''}${l[0] || ''}`.toUpperCase();
};
